/**
 * Helpers for the iOS standalone-PWA stale-viewport bug: launches/relaunches
 * can open the window a status-bar-inset short, and JS reads the same stale
 * height until WebKit recomputes the geometry (see AppHeightSync).
 */

/** Letterbox deficit range (px) that reads as the iOS stale-window bug. */
export const LETTERBOX_MIN = 20;
export const LETTERBOX_MAX = 120;

/** Query marker carried across the one hard navigation used to reset iOS. */
export const VIEWPORT_RESET_PARAM = "__pf_vfix";

interface AppViewportMeasurement {
  innerHeight: number;
  visualHeight: number;
  visualScale: number;
  editing: boolean;
}

interface KeyboardEditorLike {
  tagName?: string;
  type?: string;
  isContentEditable?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
}

const SOFTWARE_KEYBOARD_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "password",
  "tel",
  "url",
  "number",
]);

/** Whether focusing this element is expected to open a software keyboard. */
export function isSoftwareKeyboardEditor(element: KeyboardEditorLike | null): boolean {
  if (!element || element.disabled || element.readOnly) return false;
  if (element.isContentEditable) return true;
  const tagName = element.tagName?.toLowerCase();
  if (tagName === "textarea") return true;
  if (tagName !== "input") return false;
  return SOFTWARE_KEYBOARD_INPUT_TYPES.has((element.type ?? "text").toLowerCase());
}

/**
 * Keep fit screens inside the software keyboard while preserving the larger
 * layout viewport during pinch zoom and ordinary browser-chrome changes.
 */
export function appHeightForViewport({
  innerHeight,
  visualHeight,
  visualScale,
  editing,
}: AppViewportMeasurement): number {
  const layout = Math.round(innerHeight);
  const visual = Math.round(visualHeight);
  if (editing && visualScale === 1 && visual > 0 && visual < layout) return visual;
  return Math.max(layout, visual);
}

/** Whether this document was loaded by the guarded viewport-reset navigation. */
export function hasViewportResetMarker(href: string): boolean {
  return new URL(href).searchParams.has(VIEWPORT_RESET_PARAM);
}

/** Add the reset marker without disturbing the current route, query, or hash. */
export function withViewportResetMarker(href: string): string {
  const url = new URL(href);
  url.searchParams.set(VIEWPORT_RESET_PARAM, "1");
  return url.href;
}

/** Remove the internal marker after the replacement document has loaded. */
export function withoutViewportResetMarker(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(VIEWPORT_RESET_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/** Full window height the standalone app should have, orientation-aware (iOS keeps screen.* portrait-fixed). */
export function expectedStandaloneHeight(): number {
  const portrait = window.matchMedia?.("(orientation: portrait)").matches ?? true;
  return portrait
    ? Math.max(screen.height, screen.width)
    : Math.min(screen.height, screen.width);
}

/** Whether the current standalone window is open a status-bar-sized deficit short. */
export function isLetterboxed(): boolean {
  if (!isStandalone()) return false;
  const deficit = expectedStandaloneHeight() - window.innerHeight;
  return deficit >= LETTERBOX_MIN && deficit <= LETTERBOX_MAX;
}
